import { Box, Tooltip as ChakraTooltip } from "@chakra-ui/react";
import { useRestakeApy } from "@hooks/useRestakeApy";
import useTVL from "@hooks/useTVL";
import { Typography } from "@kelp-dao/kernel-design-system";
import React, { useState, useEffect, useRef } from "react";
import { numberToCurrency } from "utils";

// Reusable tooltip row component
const TooltipRow = ({
  label,
  value,
  hasBorder = false,
}: {
  label: string;
  value: string;
  hasBorder?: boolean;
}) => (
  <Box
    display="flex"
    justifyContent="space-between"
    gap={3}
    fontSize="12px"
    fontWeight="400"
    color="#4A6A6A"
    borderBottom={hasBorder ? "1px dashed" : "none"}
    borderColor="text.secondary"
    pb={hasBorder ? "8px" : "0"}
  >
    <Box>{label}</Box>
    <Box>{value}</Box>
  </Box>
);

// Stat item with tooltip - supports hover (desktop) and click (mobile)
const StatWithTooltip = ({
  label,
  value,
  tooltipContent,
  variant = "default",
  justify = "justify-start",
}: {
  label: string;
  value: string;
  tooltipContent: React.ReactNode;
  variant?: "default" | "success" | "warning" | "error";
  justify?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);

  // Close tooltip when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isOpen]);

  const getVariantClass = () => {
    const variants = {
      success: "text-feedback-success",
      warning: "text-feedback-warning",
      error: "text-feedback-error",
      default: "text-text-primary",
    };
    return variants[variant] || variants.default;
  };

  return (
    <div>
      <Typography
        type="body"
        variant="small"
        className="text-text-secondary mb-0.5 text-[12px] 2xl:text-[14px] text-left"
      >
        {label}
      </Typography>
      <div className={`flex items-center ${justify} gap-2`}>
        <ChakraTooltip
          placement="bottom-end"
          bg="backgroundCard"
          color="textSecondary"
          fontSize="12px"
          p="8px"
          borderRadius="8px"
          label={tooltipContent}
          isOpen={isOpen}
          onOpen={() => setIsOpen(true)}
          onClose={() => setIsOpen(false)}
        >
          <span
            ref={triggerRef}
            className="inline-block cursor-pointer"
            onClick={() => setIsOpen(!isOpen)}
          >
            <Typography
              type="heading"
              variant="xsmall"
              className={`${getVariantClass()} underline decoration-dotted underline-offset-8 text-[16px] 2xl:text-[20px] font-sans`}
            >
              {value}
            </Typography>
          </span>
        </ChakraTooltip>
      </div>
    </div>
  );
};

const HeaderStats = () => {
  const tvl = useTVL();
  const { roundedTotalAPY, is7DayApy, is90DayApy } = useRestakeApy();

  const apyWindowLabel = is7DayApy ? "7-day" : is90DayApy ? "90-day" : "30-day";

  const formattedTVL = tvl?.tvl?.usdTvl
    ? `$${numberToCurrency(tvl.tvl.usdTvl.toString())}`
    : "-";

  const tvlTooltipContent = (
    <div className="flex flex-col gap-2 p-3 rounded-lg">
      <TooltipRow label="Total Value Locked" value="" hasBorder />
      <TooltipRow
        label="ETH Value:"
        value={
          tvl?.tvl?.value
            ? `${numberToCurrency(tvl.tvl.value.toString())}`
            : "-"
        }
      />
      <TooltipRow
        label="USD Value:"
        value={
          tvl?.tvl?.usdTvl
            ? `$${numberToCurrency(tvl.tvl.usdTvl.toString())}`
            : "-"
        }
      />
    </div>
  );

  const aprTooltipContent = (
    <Box p={3} fontSize="12px" fontWeight="400" color="#4A6A6A">
      {`${apyWindowLabel} moving average`}
    </Box>
  );

  return (
    <div className="flex flex-row gap-5 text-center lg:text-left">
      <StatWithTooltip
        label="TVL"
        value={formattedTVL}
        tooltipContent={tvlTooltipContent}
        justify="justify-start"
      />
      <StatWithTooltip
        label="APR"
        value={roundedTotalAPY ?? "-"}
        tooltipContent={aprTooltipContent}
        justify="justify-end"
      />
    </div>
  );
};

export default HeaderStats;
